import type { SuperAdminRequestCountsResponse } from "@nlc/shared-types";

import { useQuery } from "@tanstack/react-query";

import { requestListKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";
import apiClient from "@/lib/api/client";

interface SuperAdminRequestCounts {
  byCategory?: Record<
    string,
    {
      completed: number;
      dueToday: number;
      open: number;
      overdue: number;
      paused: number;
      queued: number;
      readyForReview: number;
    }
  >;
  completed: number;
  dueToday: number;
  open: number;
  overdue: number;
  paused: number;
  queued: number;
  readyForReview: number;
}

interface UseSuperAdminRequestCountsOptions {
  enabled?: boolean;
  search?: string;
}

export function useSuperAdminRequestCounts(
  options: UseSuperAdminRequestCountsOptions = {},
) {
  const { data: userData } = useUser();
  const { enabled = true, search } = options;

  return useQuery({
    enabled: !!userData?.user && enabled,
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
    queryFn: async () => {
      const response = await apiClient
        .url("/requests/list/counts")
        .query({ search })
        .get()
        .json<SuperAdminRequestCountsResponse>();

      const counts: SuperAdminRequestCounts = {
        byCategory: response.byCategory,
        completed: response.counts.completed,
        dueToday: response.counts.dueToday,
        open: response.counts.open,
        overdue: response.counts.overdue,
        paused: response.counts.paused,
        queued: response.counts.queued,
        readyForReview: response.counts.readyForReview,
      };

      return counts;
    },
    queryKey: [...requestListKeys.all, "superadmin-counts", search],
    refetchInterval: (data) =>
      data && document.visibilityState === "visible" ? 2 * 60 * 1000 : false, // 2 minutes
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}
